import { useEffect, useState } from "react";
import { Copy, MessageSquare, Share2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { fmtQty, invApi } from "./api";

const PHONE_KEY = "bk_supplier_phone";

export function ShoppingListView({ token }) {
  const [items, setItems] = useState(null);
  const [qty, setQty] = useState({});
  const [phone, setPhone] = useState(() => localStorage.getItem(PHONE_KEY) || "");

  useEffect(() => {
    invApi(token).get("/inventory/shopping-list").then((r) => {
      setItems(r.data.items);
      setQty(Object.fromEntries(r.data.items.map((i) => [i.id, String(i.suggested_qty)])));
    }).catch(() => toast.error("Couldn't load the shopping list"));
  }, [token]);

  const text = () => {
    const lines = (items || []).filter((i) => parseFloat(qty[i.id]) > 0).map((i) => `• ${i.name} — ${fmtQty(qty[i.id])} ${i.unit}`);
    return `Blossom's Kitchen order (${new Date().toLocaleDateString()}):\n${lines.join("\n")}\n\nThanks!`;
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(text()); toast.success("Copied — paste it anywhere"); }
    catch { toast.error("Couldn't copy"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Supplier order", text: text() }); } catch { /* cancelled */ }
    } else copy();
  };
  const sms = () => {
    localStorage.setItem(PHONE_KEY, phone);
    const num = phone.replace(/\D/g, "");
    const sep = /iPhone|iPad|Mac/i.test(navigator.userAgent) ? "&" : "?";
    window.location.href = `sms:${num}${sep}body=${encodeURIComponent(text())}`;
  };

  if (!items) return <div className="text-center text-stone-400 py-10 text-sm">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="shopping-list-view">
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-2">
          <ShoppingCart className="w-4 h-4 text-brand-red" />
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Shopping list</div>
          <span className="ml-auto text-[11px] text-stone-500">{items.length} low-stock item{items.length === 1 ? "" : "s"}</span>
        </div>
        {items.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-stone-400" data-testid="shopping-list-empty">Everything is stocked above its threshold. Nothing to order.</div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {items.map((i) => (
              <li key={i.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`shopping-row-${i.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-stone-800 text-sm truncate">{i.name}</div>
                  <div className="text-[11px] text-stone-500">On hand <span className="text-red-600 font-bold">{fmtQty(i.current_stock)}</span> · alert at {fmtQty(i.low_stock_threshold)} {i.unit}</div>
                </div>
                <input value={qty[i.id] ?? ""} onChange={(e) => setQty((q) => ({ ...q, [i.id]: e.target.value }))} type="number" step="any" min="0" inputMode="decimal" data-testid={`shopping-qty-${i.id}`}
                  className="w-20 px-2 py-1.5 rounded-xl border-2 border-stone-200 text-right font-black tabular-nums outline-none focus:border-brand-red" />
                <span className="text-xs text-stone-500 font-bold w-10">{i.unit}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {items.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-4 space-y-3" data-testid="shopping-actions">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Send to your supplier</div>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="Supplier phone (optional — remembered on this device)" data-testid="supplier-phone-input"
            className="w-full px-3 py-2.5 rounded-xl border-2 border-stone-200 text-sm outline-none focus:border-brand-red" />
          <div className="grid grid-cols-3 gap-2">
            <button type="button" onClick={sms} data-testid="shopping-text-btn" className="py-3 rounded-full bg-brand-red text-white text-[10px] uppercase tracking-widest font-black flex items-center justify-center gap-1"><MessageSquare className="w-4 h-4" /> Text it</button>
            <button type="button" onClick={share} data-testid="shopping-share-btn" className="py-3 rounded-full bg-stone-800 text-white text-[10px] uppercase tracking-widest font-black flex items-center justify-center gap-1"><Share2 className="w-4 h-4" /> Share</button>
            <button type="button" onClick={copy} data-testid="shopping-copy-btn" className="py-3 rounded-full border-2 border-stone-200 text-stone-700 text-[10px] uppercase tracking-widest font-black flex items-center justify-center gap-1"><Copy className="w-4 h-4" /> Copy</button>
          </div>
          <pre className="text-xs text-stone-600 bg-stone-50 rounded-xl p-3 whitespace-pre-wrap font-sans" data-testid="shopping-preview">{text()}</pre>
        </div>
      )}
    </div>
  );
}
